import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useState,
  Ref,
  Dispatch,
  SetStateAction,
} from 'react';
import { compareFiles } from '../../../utilities';
import { ImageTypeModel } from '../CreateMemorialQuiltStepThree/model';
import Cropper from 'cropperjs';
import { setOpen } from 'store/reducers/notifySlice';
import { useAppDispatch } from 'hooks';
import { checkAndParseImages } from '../helpers/check-and-parse-images';
import { ImageEditorBlock } from './ImageEditorBlock';

interface CreateImagesBlockLocalProps {
  setImageBlock: (image: ImageTypeModel | null) => void;
  isAddImagesOnly?: boolean;
  isStepOne?: boolean;
  setFilesImages: Dispatch<SetStateAction<File[]>>;
  filesImages: File[];
  imageName?: string | null;
  showAddImages?: boolean;
}

export const CreateImagesBlockLocal = forwardRef<HTMLDivElement, CreateImagesBlockLocalProps>(
  (
    {
      setImageBlock,
      isAddImagesOnly,
      isStepOne,
      setFilesImages,
      filesImages,
      imageName,
      showAddImages,
    },
    ref: Ref<HTMLDivElement>
  ) => {
    const dispatch = useAppDispatch();
    const [currentFileImage, setCurrentFileImage] = useState<File | null>(() => null);
    const [currentCrop, setCurrentCrop] = useState<Cropper | null>(null);
    const [originalImage, setOriginalImage] = useState<string | null>(null);
    const [showClearImagesDialog, setShowClearImagesDialog] = useState<boolean>(false);

    const changeImages = async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files) return;

      // check the same images
      const filesDataUnique: File[] = [];
      const parsedImages = await checkAndParseImages(files);

      if (filesImages.length === 0) {
        filesDataUnique.push(...parsedImages);
      } else {
        for (const newFile of parsedImages) {
          let isNewFile = true;

          for (const wasFile of filesImages) {
            const isSameFile = await compareFiles(newFile, wasFile);

            if (isSameFile) {
              dispatch(
                setOpen(`You already have the same picture! You can\`t add ${newFile.name}`)
              );
              isNewFile = false;
              break;
            }
          }

          if (isNewFile) filesDataUnique.push(newFile);
        }
      }

      setFilesImages((state) => {
        return [...state, ...filesDataUnique];
      });
    };

    const resetImages = () => {
      setFilesImages([]);
      setShowClearImagesDialog(false);
    };

    const deleteFileImage = async (fileIndex: number) => {
      // for local delete
      setFilesImages((state) =>
        state ? state.filter((item, index) => index !== fileIndex) : state
      );
    };

    useEffect(() => {
      if (!imageName) return;

      setOriginalImage(imageName);
      const image = filesImages.find((photo) => photo.name === imageName);
      setCurrentFileImage(image ?? null);
    }, [imageName, showAddImages]);

    return (
      <ImageEditorBlock 
        isLocal
        isAddImagesOnly={isAddImagesOnly}
        ref={ref}
        showClearImagesDialog={showClearImagesDialog}
        setShowClearImagesDialog={setShowClearImagesDialog}
        resetImages={resetImages}
        changeImages={changeImages}
        filesImages={filesImages}
        deleteFileImage={deleteFileImage}
        currentFileImage={currentFileImage}
        isStepOne={isStepOne}
        currentCrop={currentCrop}
        originalImage={originalImage}
        setImageBlock={setImageBlock}
        isMain={true}
        setCurrentCrop={setCurrentCrop}
        setCurrentFileImage={setCurrentFileImage}
        setOriginalImage={setOriginalImage}
      />
    );
  }
);
